import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { NominationFormContext } from './NominationFormContext';
import { useQuery } from '@percent/grants-nomination-form/hooks';
import { getNominationFormAction } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.actions';
import { useParams } from 'react-router-dom';
import { NominationFormState, QuestionVisibility } from './NominationFormContext.types';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE } from '@percent/grants-nomination-form/i18n/resources';

export const NominationFormContextController = ({ children }: PropsWithChildren) => {
  const [visibleQuestions, setVisibleQuestionsState] = useState<QuestionVisibility>({});
  const [nominationFormState, setNominationFormState] = useState<NominationFormState>('initial');
  const { programId } = useParams();
  const { i18n } = useTranslation();

  const {
    data: nominationFormData,
    isPending: isLoading,
    isError,
  } = useQuery(['getNominationForm', { programId, locale: i18n.language }], getNominationFormAction, { retry: false });

  useEffect(() => {
    if (isError) {
      setNominationFormState('error');
    }
  }, [isError]);

  useEffect(() => {
    if (nominationFormData?.data && !nominationFormData?.data?.isSupportedLocale) {
      i18n.changeLanguage(DEFAULT_LOCALE);
    }
  }, [nominationFormData?.data, i18n]);

  // Merge partial visibility updates so multiple selects on the same page don't overwrite each other's state.
  const setVisibleQuestions = useCallback((questions: QuestionVisibility) => {
    setVisibleQuestionsState((prev) => ({ ...prev, ...questions }));
  }, []);

  const filteredNominationFormData = useMemo(() => {
    if (!nominationFormData?.data) return undefined;

    return {
      ...nominationFormData.data,
      pages: nominationFormData.data.pages
        .sort((a, b) => a.order - b.order)
        .map((page) => ({
          ...page,
          questions: page.questions?.filter((question) => !question.hidden || visibleQuestions[question.id]),
        })),
    };
  }, [nominationFormData, visibleQuestions]);

  const contextValue = useMemo(
    () => ({
      nominationFormData: filteredNominationFormData,
      isLoading,
      nominationFormState,
      setNominationFormState,
      setVisibleQuestions,
    }),
    [isLoading, nominationFormState, filteredNominationFormData, setVisibleQuestions],
  );

  return <NominationFormContext.Provider value={contextValue}>{children}</NominationFormContext.Provider>;
};
